// Or from '@reduxjs/toolkit/query' if not using the auto-generated hooks
import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from 'redux/reducers/settings';
import { PaginatedDataResponse, Response } from 'common/types';

import { CoursesResponse, IPageQueryParams } from '../courses';
import { ITrainingTypeResponse } from '../training-type';

interface ITrainingResponse extends ITrainingInput {
  created_at: string;
  updated_at: string;
  course_details: CoursesResponse;
  training_type_details: ITrainingTypeResponse;
  training_code: string;
}

export interface ITrainingInput {
  training_name: string;
  training_description: string;
  training_code: string;
  center: number;
  courses: string[];
  training_id?: number;
  image?: File[];
  id: number;
}

export const trainingApi: { [key: string]: any } = createApi({
  reducerPath: 'trainingApi',
  baseQuery: baseQuery,
  tagTypes: ['Training', 'TrainingCourseTopics'],
  endpoints: (build) => ({
    //     listTraining: build.query<Response<ITrainingInput[]>, IPageQueryParams | null>({
    //   query: (params) =>
    //     params?.searchQuery
    //       ? `/training/?search=${params.searchQuery}`
    //       : `/training/`,

    //   transformResponse: (res: Response<ITrainingInput[]>) => res,

    //   providesTags: () => [{ type: 'Training', id: 'PARTIAL-LIST' }]
    // }),
    listTraining: build.query<Response<ITrainingInput[]>, IPageQueryParams | null>({
      query: (params) =>
        params?.searchQuery ? `/training/?search=${params.searchQuery}` : `/training/`,

      transformResponse: (res: Response<ITrainingInput[]>) => ({
        ...res,
        data: Array.isArray(res?.data) ? res.data : []
      }),

      providesTags: (result) => {
        if (result && Array.isArray(result.data)) {
          return [
            ...result.data.map((t) => ({
              type: 'Training' as const,
              id: t.id
            })),
            { type: 'Training', id: 'LIST' }
          ];
        }

        return [{ type: 'Training', id: 'LIST' }];
      }
    }),

    // listTrainingById: build.query<ITrainingInput, string>({
    //   query: (id) => `training/detail/${id}/`,
    //   transformResponse: (res: Response<ITrainingInput>) => res.data
    // }),
    listTrainingById: build.query<Response<ITrainingInput>, string>({
      query: (id) => `training/detail/${id}/`,
      transformResponse: (res: Response<ITrainingInput>) => res
    }),

    createTraining: build.mutation<ITrainingResponse, ITrainingInput>({
      query(body) {
        const formData = new FormData();
        Object.entries(body).forEach(([key, value]) => {
          if (value instanceof File) formData.append(key, value);
          else if (Array.isArray(value)) value.forEach((v) => formData.append(key, v));
          else formData.append(key, value?.toString() ?? '');
        });

        return {
          url: `training/create/`,
          method: 'POST',
          body: formData
        };
      },
      invalidatesTags: ['Training']
    }),

    updateTraining: build.mutation<ITrainingResponse, ITrainingInput>({
      query(body) {
        const formData = new FormData();
        Object.entries(body).forEach(([key, value]) => {
          if (value instanceof File) formData.append(key, value);
          else if (Array.isArray(value)) value.forEach((v) => formData.append(key, v));
          else formData.append(key, value?.toString() ?? '');
        });

        return {
          url: `training/update/${body.id}/`,
          method: 'PATCH',
          body: formData
        };
      },
      invalidatesTags: ['Training']
    }),
    addStartImage: build.mutation<any, any>({
      query({ id, start_training_image }) {
        let formData = new FormData();
        formData.append('start_training_image', start_training_image);
        return {
          url: `training/${id}/start-training-image`,
          method: 'POST',
          body: formData
        };
      },
      invalidatesTags: [{ type: 'Training', id: 'PARTIAL-LIST' }]
    }),
    addEndImage: build.mutation<any, any>({
      query({ id, end_training_image }) {
        let formData = new FormData();
        formData.append('end_training_image', end_training_image);
        return {
          url: `training/${id}/end-training-image`,
          method: 'POST',
          body: formData
        };
      },
      invalidatesTags: [{ type: 'Training', id: 'PARTIAL-LIST' }]
    }),

    deleteTraining: build.mutation<Response<any>, string>({
      query(id) {
        return {
          url: `training/delete/${id}/`,
          method: 'DELETE'
        };
      },
      invalidatesTags: ['Training']
    }),

    listTrainingCourseTopics: build.query<Response<any[]>, string>({
      query: (trainingId) => `training/course-topics/${trainingId}/`,
      transformResponse: (res: Response<any[]>) => res
    }),
    listTrainingCourse: build.query<Response<any[]>, string>({
      query: () => `training/dropdown/routinemaster`,
      transformResponse: (res: Response<any[]>) => res
    })
  })
});

export const {
  useListTrainingQuery,
  useListTrainingByIdQuery,
  useCreateTrainingMutation,
  useUpdateTrainingMutation,
  useDeleteTrainingMutation,
  useListTrainingCourseTopicsQuery,
  useListTrainingCourseQuery,
  useAddStartImageMutation,
  useAddEndImageMutation
} = trainingApi;
